// src/lib/mixedSnapshot.ts
// Mixed-Use-Snapshot, gleiches Muster wie src/lib/etwSnapshot.ts (siehe dort fuer
// die Begruendung des fehlenden Cross-File-Imports). Mixed-Use hat zwei parallele
// Eingabe-Bloecke (Wohnen "w*" und Gewerbe "g*"), kein Array -- beide Bloecke sind
// immer vorhanden, nicht optional wie MFHs mgmtMode.
//
// Reparatur-Hinweis: Der bisherige JSON-/PDF-Import las ausschliesslich "kaufpreis"
// und ignorierte alle anderen Felder (Flaechen, Mieten, Finanzierung, NK, Spielwiese)
// komplett -- praktisch kein funktionierender Import. Diese Datei und die Aenderungen
// in MixedUseCheck.tsx beheben das.

export type AnalyzerTypeId = "etw" | "mfh" | "efh" | "gewerbe" | "mixeduse";

/** confirmedFields: Phase-4 Runde 2b, Baustein 2 -- siehe etwSnapshot.ts fuer die
 *  ausfuehrliche Begruendung. Optional/abwaertskompatibel. */
export type DealSnapshotEnvelope<TInput, TPlayground> = {
  version: number;
  analyzerType: AnalyzerTypeId;
  input: TInput;
  playground: TPlayground;
  confirmedFields: string[];
};

function resolveField<T>(raw: Record<string, unknown> | undefined, key: string, fallback: T): T {
  const value = raw?.[key];
  if (value === undefined || value === null) return fallback;
  if (typeof fallback === "number") return typeof value === "number" && Number.isFinite(value) ? (value as T) : fallback;
  if (typeof fallback === "boolean") return typeof value === "boolean" ? (value as T) : fallback;
  if (typeof fallback === "string") return typeof value === "string" ? (value as T) : fallback;
  return (value as T) ?? fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function resolveConfirmedFields(raw: Record<string, unknown> | undefined, key: string): string[] {
  const value = raw?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

export const MIXED_SNAPSHOT_VERSION = 1;

export type MixedSnapshotInput = {
  kaufpreis: number;
  financingOn: boolean;
  /** Vereinheitlichte Darlehensregel (16.10.2026, Dominiks Vorgabe): Eigenkapital in Euro
   *  statt Beleihungsquote (LTV) -- wie MFH/EFH/Gewerbe. Kredit = Kaufpreis − max(0, EK − NK). */
  eigenkapital: number;
  /** Migrations-Bruecke: bei einem alten Snapshot/Export (vor der Umstellung auf
   *  eigenkapital) der frueher gespeicherte ltvPct-Wert, sonst null. Aufgeloest in
   *  core/score/mixedDealScore.ts' resolveMixedEigenkapital(), sobald die Nebenkosten
   *  bekannt sind (eigenes Subsystem, hier nicht verfuegbar). */
  legacyLtvPct: number | null;
  zinsPct: number;
  tilgungPct: number;
  // Wohnen
  wFl: number;
  wRentM2: number;
  wLeer: number;
  wOpexBrutto: number;
  wCap: number;
  // Gewerbe
  gFl: number;
  gRentM2: number;
  gLeer: number;
  gOpexBrutto: number;
  gCap: number;
  // Bewirtschaftung Pauschal/Einzeln (Baustein 5): identisches Muster wie MfhSnapshotInput
  // (src/lib/mfhSnapshot.ts) -- null = kein Modus gespeichert. ANDERS als MFH/EFH/Gewerbe: die
  // Migrationsentscheidung (core/score/mixedDealScore.ts' resolveMixedBewirtschaftung()) hat
  // hier KEINEN "war Instandhaltung je 0"-Anhaltspunkt (Gemischt hatte nie ein eigenes
  // Instandhaltungsfeld) -- alte Snapshots werden dort deshalb unconditional "einzeln". Je
  // Segment eigene Felder, da § 28 II. BV nur fuer den Wohnteil gilt (kein Vorschlag fuer
  // Gewerbe).
  bewirtschaftungModus: "pauschal" | "einzeln" | null;
  wNichtUmlagefaehig: number;
  wVerwaltung: number;
  wInstandhaltungJeM2Jahr: number;
  baujahr: number | null;
  hatAufzug: boolean;
  gNichtUmlagefaehig: number;
  gVerwaltung: number;
  gInstandhaltungJeM2Jahr: number;
  /** Deal-Score v2 Leerstand-Obergrenze. Alte Snapshots ohne dieses Feld gelten als "vollstaendig". */
  vermietet: "vollstaendig" | "teilweise" | "nein";
};

export type MixedSnapshotPlayground = {
  priceAdjPct: number;
  wRentAdjPct: number;
  gRentAdjPct: number;
  applyAdjustments: boolean;
};

export type MixedSnapshot = DealSnapshotEnvelope<MixedSnapshotInput, MixedSnapshotPlayground>;

// UI-Defaults, siehe MixedUseCheck.tsx (Beispielobjekt).
export const MIXED_INPUT_DEFAULTS: MixedSnapshotInput = {
  kaufpreis: 2_400_000,
  financingOn: true,
  eigenkapital: 1_368_000,
  legacyLtvPct: null,
  zinsPct: 0.041,
  tilgungPct: 0.02,
  wFl: 750,
  wRentM2: 13,
  wLeer: 0.06,
  wOpexBrutto: 0.25,
  wCap: 0.055,
  gFl: 450,
  gRentM2: 20,
  gLeer: 0.1,
  gOpexBrutto: 0.3,
  gCap: 0.065,
  bewirtschaftungModus: null,
  wNichtUmlagefaehig: 0,
  wVerwaltung: 0,
  wInstandhaltungJeM2Jahr: 0,
  baujahr: null,
  hatAufzug: false,
  gNichtUmlagefaehig: 0,
  gVerwaltung: 0,
  gInstandhaltungJeM2Jahr: 0,
  vermietet: "vollstaendig",
};

export const MIXED_PLAYGROUND_DEFAULTS: MixedSnapshotPlayground = {
  priceAdjPct: 0,
  wRentAdjPct: 0,
  gRentAdjPct: 0,
  applyAdjustments: true,
};

export function buildMixedSnapshot(
  input: MixedSnapshotInput,
  playground: MixedSnapshotPlayground,
  confirmedFields: string[] = []
): MixedSnapshot {
  return { version: MIXED_SNAPSHOT_VERSION, analyzerType: "mixeduse", input, playground, confirmedFields };
}

export type ResolvedMixedSnapshot = {
  input: MixedSnapshotInput;
  playground: MixedSnapshotPlayground;
  /** false, wenn Kaufpreis oder eine der beiden Flaechen/Mieten (Wohnen/Gewerbe) fehlt. */
  complete: boolean;
  confirmedFields: string[];
};

export function resolveMixedSnapshot(raw: unknown): ResolvedMixedSnapshot {
  const r = asRecord(raw);
  const rawInput = asRecord(r.input);
  const rawPlayground = asRecord(r.playground);

  const complete =
    typeof rawInput.kaufpreis === "number" &&
    typeof rawInput.wFl === "number" &&
    typeof rawInput.wRentM2 === "number" &&
    typeof rawInput.gFl === "number" &&
    typeof rawInput.gRentM2 === "number";

  const input: MixedSnapshotInput = {
    kaufpreis: resolveField(rawInput, "kaufpreis", MIXED_INPUT_DEFAULTS.kaufpreis),
    financingOn: resolveField(rawInput, "financingOn", MIXED_INPUT_DEFAULTS.financingOn),
    eigenkapital: resolveField(rawInput, "eigenkapital", MIXED_INPUT_DEFAULTS.eigenkapital),
    legacyLtvPct:
      (rawInput?.eigenkapital === undefined || rawInput?.eigenkapital === null) &&
      typeof rawInput?.ltvPct === "number" && Number.isFinite(rawInput.ltvPct)
        ? (rawInput.ltvPct as number)
        : null,
    zinsPct: resolveField(rawInput, "zinsPct", MIXED_INPUT_DEFAULTS.zinsPct),
    tilgungPct: resolveField(rawInput, "tilgungPct", MIXED_INPUT_DEFAULTS.tilgungPct),
    wFl: resolveField(rawInput, "wFl", MIXED_INPUT_DEFAULTS.wFl),
    wRentM2: resolveField(rawInput, "wRentM2", MIXED_INPUT_DEFAULTS.wRentM2),
    wLeer: resolveField(rawInput, "wLeer", MIXED_INPUT_DEFAULTS.wLeer),
    wOpexBrutto: resolveField(rawInput, "wOpexBrutto", MIXED_INPUT_DEFAULTS.wOpexBrutto),
    wCap: resolveField(rawInput, "wCap", MIXED_INPUT_DEFAULTS.wCap),
    gFl: resolveField(rawInput, "gFl", MIXED_INPUT_DEFAULTS.gFl),
    gRentM2: resolveField(rawInput, "gRentM2", MIXED_INPUT_DEFAULTS.gRentM2),
    gLeer: resolveField(rawInput, "gLeer", MIXED_INPUT_DEFAULTS.gLeer),
    gOpexBrutto: resolveField(rawInput, "gOpexBrutto", MIXED_INPUT_DEFAULTS.gOpexBrutto),
    gCap: resolveField(rawInput, "gCap", MIXED_INPUT_DEFAULTS.gCap),
    bewirtschaftungModus: rawInput.bewirtschaftungModus === "pauschal" || rawInput.bewirtschaftungModus === "einzeln" ? rawInput.bewirtschaftungModus : null,
    wNichtUmlagefaehig: resolveField(rawInput, "wNichtUmlagefaehig", MIXED_INPUT_DEFAULTS.wNichtUmlagefaehig),
    wVerwaltung: resolveField(rawInput, "wVerwaltung", MIXED_INPUT_DEFAULTS.wVerwaltung),
    wInstandhaltungJeM2Jahr: resolveField(rawInput, "wInstandhaltungJeM2Jahr", MIXED_INPUT_DEFAULTS.wInstandhaltungJeM2Jahr),
    baujahr: typeof rawInput.baujahr === "number" && Number.isFinite(rawInput.baujahr) ? rawInput.baujahr : null,
    hatAufzug: resolveField(rawInput, "hatAufzug", MIXED_INPUT_DEFAULTS.hatAufzug),
    gNichtUmlagefaehig: resolveField(rawInput, "gNichtUmlagefaehig", MIXED_INPUT_DEFAULTS.gNichtUmlagefaehig),
    gVerwaltung: resolveField(rawInput, "gVerwaltung", MIXED_INPUT_DEFAULTS.gVerwaltung),
    gInstandhaltungJeM2Jahr: resolveField(rawInput, "gInstandhaltungJeM2Jahr", MIXED_INPUT_DEFAULTS.gInstandhaltungJeM2Jahr),
    vermietet:
      rawInput.vermietet === "teilweise" || rawInput.vermietet === "nein"
        ? rawInput.vermietet
        : "vollstaendig",
  };

  const playground: MixedSnapshotPlayground = {
    priceAdjPct: resolveField(rawPlayground, "priceAdjPct", resolveField(rawInput, "priceAdjPct", MIXED_PLAYGROUND_DEFAULTS.priceAdjPct)),
    wRentAdjPct: resolveField(rawPlayground, "wRentAdjPct", resolveField(rawInput, "wRentAdjPct", MIXED_PLAYGROUND_DEFAULTS.wRentAdjPct)),
    gRentAdjPct: resolveField(rawPlayground, "gRentAdjPct", resolveField(rawInput, "gRentAdjPct", MIXED_PLAYGROUND_DEFAULTS.gRentAdjPct)),
    applyAdjustments: resolveField(rawPlayground, "applyAdjustments", resolveField(rawInput, "applyAdjustments", MIXED_PLAYGROUND_DEFAULTS.applyAdjustments)),
  };

  const confirmedFields = resolveConfirmedFields(r, "confirmedFields");

  return { input, playground, complete, confirmedFields };
}
